import { CircleLoader, EmptyState, IconError, Space } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { type ReportAccessGrant } from '@webapp/platform/iam/entities/ReportAccessGrant'
import messages from './messages'
import { ClearAllButtonStyled } from './styles/ClearAllButtonStyled'
import { HighlightableTagStyled } from './styles/HighlightableTagStyled'
import { TagRemoveButtonStyled } from './styles/TagRemoveButtonStyled'

export interface IReportAccessGrantListViewProps {
  grants: ReportAccessGrant[]
  highlightedGrantId: number | undefined
  isLoading: boolean
  onRemoveGrant: (grant: ReportAccessGrant) => void
  onClearAll: (grants: ReportAccessGrant[]) => void
}

export const ReportAccessGrantListView = ({
  grants,
  highlightedGrantId,
  isLoading,
  onRemoveGrant,
  onClearAll,
}: IReportAccessGrantListViewProps) => {
  const intl = useProcurifyIntl()

  if (isLoading) {
    return <CircleLoader />
  }

  return (
    <Space>
      <div>
        <ClearAllButtonStyled
          type='button'
          $hasGrants={grants.length > 0}
          onClick={() => onClearAll(grants)}
        >
          {intl.formatMessage(messages.clearAllLabel, {
            count: grants.length,
          })}
        </ClearAllButtonStyled>
      </div>

      {grants.length === 0 ? (
        <EmptyState
          header={intl.formatMessage(messages.emptyStateHeader)}
          content={intl.formatMessage(messages.emptyStateContent)}
        />
      ) : (
        <Space direction='horizontal' size='small' flexWrap>
          {grants.map((grant) => (
            <HighlightableTagStyled
              key={grant.id}
              $isHighlighted={grant.id === highlightedGrantId}
            >
              {grant.userFullName}
              <TagRemoveButtonStyled
                type='button'
                aria-label={intl.formatMessage(messages.removeGrantLabel, {
                  userFullName: grant.userFullName,
                })}
                onClick={() => onRemoveGrant(grant)}
              >
                <IconError size={12} />
              </TagRemoveButtonStyled>
            </HighlightableTagStyled>
          ))}
        </Space>
      )}
    </Space>
  )
}

ReportAccessGrantListView.displayName = 'ReportAccessGrantListView'
